import { useState, useCallback, useMemo } from 'react'
import { useLocation } from '../../../components/Location'
import { EVENT_LOCATION_TYPE, IAddressComponent, ILocationQuiz, IPlace } from 'src/types'

export const useAddressInput = ({ setCheckSuccess }: { setCheckSuccess: (checkSuccess: boolean) => void }) => {
  const [addressError, setAddressError] = useState('')
  const [addressInputValue, setAddressInputValue] = useState('')
  const [isAddressValid, setIsAddressValid] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<IPlace | null>(null)
  const [isPlaceSelected, setIsPlaceSelected] = useState(false)

  const location = useLocation()
  const { handleGGPlaceSelected, handleChange, locationSelected } = location

  const onUnServiceableAddressStatus = () => {
    const errorMessage = `Sorry, we don't have enough time to prepare for your event.`
    setAddressError(errorMessage)
    setIsAddressValid(false)
    setSelectedPlace(null)
  }
  const onServiceableAddressSuccessStatus = () => {
    setIsAddressValid(true)
  }
  const onDateChange = () => {
    setIsAddressValid(false)
    setCheckSuccess(false)
  }

  const onServiceableAddressErrorStatus = () => {
    setAddressError('Failed to check venue availability. Please try again.')
    setSelectedPlace(null)
  }
  // Parse place to event venue
  const parsePlaceToEventVenue = (place: IPlace): ILocationQuiz => {
    let streetNumber = '',
      route = '',
      city = '',
      state = '',
      zipCode = '',
      country = ''

    place?.address_components?.forEach((component: IAddressComponent) => {
      const componentType = component.types[0]
      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name || ''
          break
        case 'route':
          route = component.long_name || ''
          break
        case 'postal_code':
          zipCode = component.long_name || ''
          break
        case 'locality':
          city = component.long_name || ''
          break
        case 'administrative_area_level_1':
          state = component.short_name || ''
          break
        case 'country':
          country = component.short_name || ''
          break
      }
    })

    // Extract venue name more intelligently
    let venueName = place?.name || ''

    // If name is empty or is just the street address, try to extract from formatted_address
    if (!venueName || venueName === `${streetNumber} ${route}`.trim()) {
      // Try to get the first part of formatted_address before the first comma
      if (place?.formatted_address) {
        const firstPart = place.formatted_address.split(',')[0].trim()
        // Only use it if it's not just a street address
        if (firstPart && firstPart !== `${streetNumber} ${route}`.trim()) {
          venueName = firstPart
        }
      }
    }

    // Ensure we have at least basic location info
    if (!city && place?.formatted_address) {
      const addressParts = place.formatted_address.split(',').map((part: string) => part.trim())
      if (addressParts.length > 1) {
        // Try to extract city from formatted address
        const lastPart = addressParts[addressParts.length - 1]
        const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/)
        if (stateZipMatch) {
          state = stateZipMatch[1]
          zipCode = stateZipMatch[2] || zipCode
          city = addressParts[addressParts.length - 2] || city
        } else {
          city = lastPart
        }
      }
    }

    // Set default country if not found
    if (!country) {
      country = 'US'
    }

    return {
      type: EVENT_LOCATION_TYPE.CEREMONY,
      place: [place?.name, place?.formatted_address].filter((i) => i).join(', ') || '',
      venue: venueName,
      city: city || '',
      state: state || '',
      streetAddress: `${streetNumber} ${route}`.trim() || '',
      zipCode: zipCode || '',
      placeId: place?.place_id || '',
      country: country || 'US',
    }
  }

  // Handle place selection
  const handlePlaceSelection = useCallback(
    (place: IPlace) => {
      setSelectedPlace(place)
      handleGGPlaceSelected(place, 0)

      // Display the full formatted address for better user experience
      const displayValue = place?.formatted_address || place?.name || ''
      setAddressInputValue(displayValue)
      setIsPlaceSelected(true)

      setAddressError('')
      setIsAddressValid(false)
      setCheckSuccess(false)
    },
    [handleGGPlaceSelected, setCheckSuccess]
  )
  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e, 0)
      setAddressInputValue(e.target.value)

      // Reset place selection flag when user starts typing
      if (isPlaceSelected) {
        setIsPlaceSelected(false)
        setSelectedPlace(null)
      }

      if (e.target.value) {
        setAddressError('')
      }
      setIsAddressValid(false)
      setCheckSuccess(false)
    },
    [handleChange, isPlaceSelected, setCheckSuccess]
  )
  const isAddressFilled = useMemo(() => {
    const hasSelectedPlace = selectedPlace?.formatted_address
    const hasManualAddress = locationSelected[0]?.place && locationSelected[0].place.trim() !== ''
    const hasAddressInput = addressInputValue.trim() !== ''
    return hasSelectedPlace || hasManualAddress || hasAddressInput
  }, [selectedPlace, locationSelected, addressInputValue])

  // Extract address from address parts and addressString
  const extractAddress = (addressParts: string[], address: string) => {
    let extractedCity = ''
    let extractedState = ''
    let extractedZipCode = ''
    if (addressParts.length > 1) {
      // Try to extract city and state from the last parts
      const lastPart = addressParts[addressParts.length - 1]
      const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/)
      if (stateZipMatch) {
        extractedState = stateZipMatch[1]
        extractedZipCode = stateZipMatch[2] || ''
        extractedCity = addressParts[addressParts.length - 2] || ''
      } else {
        // If no state/zip pattern, assume last part is city
        extractedCity = lastPart
      }
    }
    const mockPlace = {
      name: addressParts[0] || '',
      formatted_address: address,
      place_id: '',
      address_components: [
        ...(extractedCity ? [{ long_name: extractedCity, types: ['locality'] }] : []),
        ...(extractedState ? [{ short_name: extractedState, types: ['administrative_area_level_1'] }] : []),
        ...(extractedZipCode ? [{ long_name: extractedZipCode, types: ['postal_code'] }] : []),
        { short_name: 'US', types: ['country'] },
      ],
    }
    const eventVenueObj = {
      type: 'CEREMONY',
      place: address,
      venue: addressParts[0] || '',
      city: extractedCity,
      state: extractedState,
      streetAddress: addressParts[0] || '',
      zipCode: extractedZipCode,
      placeId: '',
      country: 'US',
    }
    return {
      eventVenueObj,
      mockPlace,
    }
  }

  return {
    setAddressError,
    onUnServiceableAddressStatus,
    onServiceableAddressSuccessStatus,
    onServiceableAddressErrorStatus,
    parsePlaceToEventVenue,
    isPlaceSelected,
    handlePlaceSelection,
    onDateChange,
    handleAddressChange,
    isAddressFilled,
    selectedPlace,
    locationSelected,
    addressInputValue,
    extractAddress,
    isAddressValid,
    addressError,
  }
}
